import { useMemo, useCallback } from "react";

/**
 * Normalize text for filtering:
 * - Trim leading/trailing whitespace
 * - Collapse multiple spaces
 * - Convert to lowercase
 */
export const normalizeText = (text) => {
  if (!text) return "";
  return String(text)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
};

/**
 * Check if text matches query (case-insensitive, space-normalized)
 */
export const textMatches = (text, query) => {
  const normalizedText = normalizeText(text);
  const normalizedQuery = normalizeText(query);
  return normalizedText.includes(normalizedQuery);
};

/**
 * Custom hook for live filtering of items
 * 
 * @param {Array} items - Array of items to filter
 * @param {string} query - Search query string
 * @param {Function} getSearchText - Function that extracts searchable text from an item
 * @returns {Array} Filtered items
 * 
 * Usage:
 * const filteredItems = useLiveFilter(
 *   customers,
 *   searchQuery,
 *   (customer) => `${customer.name} ${customer.company}`
 * );
 */
export const useLiveFilter = (items, query, getSearchText) => {
  return useMemo(() => {
    // If no query, return all items
    if (!query || !normalizeText(query)) {
      return items;
    }
    
    const normalizedQuery = normalizeText(query);
    
    // Filter items where any searchable text matches
    return items.filter((item) => {
      const searchText = normalizeText(getSearchText(item));
      return searchText.includes(normalizedQuery);
    });
  }, [items, query, getSearchText]);
};

/**
 * Get search text extractor for customers
 */
export const getCustomerSearchText = (customer) => {
  const parts = [
    customer.name,
    customer.company,
    customer.email,
    customer.phone?.replace(/\D/g, ""), // digits only for phone
  ].filter(Boolean);
  return parts.join(" ");
};

/**
 * Get search text extractor for jobs
 */
export const getJobSearchText = (job) => {
  const parts = [
    job.title,
    job.customer?.name,
  ].filter(Boolean);
  return parts.join(" ");
};

/**
 * Get search text extractor for estimates
 */
export const getEstimateSearchText = (estimate) => {
  const parts = [
    estimate.title,
    estimate.estimate_number,
    estimate.customer?.name,
  ].filter(Boolean);
  return parts.join(" ");
};

/**
 * Get search text extractor for invoices
 */
export const getInvoiceSearchText = (invoice) => {
  const parts = [
    invoice.title,
    invoice.invoice_number,
    invoice.customer?.name,
  ].filter(Boolean);
  return parts.join(" ");
};

/**
 * Get search text extractor for follow-ups/reminders
 */
export const getFollowUpSearchText = (reminder) => {
  const parts = [
    reminder.customer_name,
    reminder.note,
    reminder.linked_entity_title,
  ].filter(Boolean);
  return parts.join(" ");
};

export default useLiveFilter;
